import { useQuery } from '@apollo/client';
import dayjs from 'dayjs';
import React from 'react';
import InputFilterItem from '../../../delivery/components/InputFilterItem';
import DateRangeFilterItem from '../../../delivery/components/DateRangeFilterItem';
import { getSmartLabelClassName } from '../../../../utils/labelWidthUtils';
import ParcelFilterStyle from '../../../delivery/components/ParcelFilterStyle';
import {
    GET_HUB_AND_WHS_OPTIONS,
    GetHubAndWhsOptionsResponse
} from '../graphql/queries/getHubAndWhsOptions';
import {
    GET_CARRIER_OPTIONS,
    GetCarrierOptionsResponse
} from '../graphql/queries/getCarrierOptions';
import { ConsolidationTaskStatus } from '../graphql/types/ConsolidationTypes';
import { useTranslation } from 'react-i18next';

export type ConsolidationFilterOptions = {
    taskNameSearch: string;
    status: ConsolidationTaskStatus | 'all';
    hubName: string;
    whsName: string;
    carrierCode?: number; // 新增Carrier字段
    createdAtRange: [dayjs.Dayjs | null, dayjs.Dayjs | null];
};

export const statusOptions = [
    { value: ConsolidationTaskStatus.CREATED, label: 'Created' },
    { value: ConsolidationTaskStatus.DISSOLVED, label: 'Discarded' },
    { value: ConsolidationTaskStatus.COMPLETED, label: 'Completed' },
];

export const statusOptionsWithHandover = [
    { value: ConsolidationTaskStatus.CREATED, label: 'Created' },
    { value: ConsolidationTaskStatus.DISSOLVED, label: 'Discarded' },
    { value: ConsolidationTaskStatus.COMPLETED, label: 'Completed' },
    { value: ConsolidationTaskStatus.HANDOVER, label: 'Handover' },
];

export const DEFAULT_FILTER_OPTIONS: ConsolidationFilterOptions = {
    taskNameSearch: '',
    status: 'all',
    hubName: '',
    whsName: '',
    carrierCode: undefined,
    createdAtRange: [dayjs().subtract(7, 'day'), dayjs()]
};

interface ConsolidationFiltersProps {
    loading?: boolean;
    pageType?: 'swiftx' | 'other-carrier'; // 新增页面类型参数
    filters: ConsolidationFilterOptions;
    setFilters: (filters: ConsolidationFilterOptions) => void;
}

const ConsolidationFilters: React.FC<ConsolidationFiltersProps> = ({ 
    loading = false,
    pageType = 'swiftx',
    filters,
    setFilters
}) => {
    const { t } = useTranslation();

    const labelTaskNumber = String(t('wms.baggingTask.filters.taskNumber'));
    const labelTaskStatus = String(t('wms.baggingTask.filters.taskStatus'));
    const labelDestinationHub = String(t(pageType === 'other-carrier' ? 'wms.baggingTask.filters.handoverHub' : 'wms.baggingTask.filters.destinationHub'));
    const labelDestinationWhs = String(t('wms.baggingTask.filters.destinationWHS'));
    const labelCarrier = String(t('wms.baggingTask.columns.carrier'));
    const labelCreationTime = String(t('wms.baggingTask.filters.creationTime'));

    const col1Labels = [labelTaskNumber, labelCreationTime];
    const col2Labels = [labelTaskStatus, labelDestinationWhs];
    const col3Labels = [labelDestinationHub, labelCarrier];

    // 在组件内部定义 statusOptions，使用翻译函数
    const statusOptions = [
        { value: 'all', label: t('components.input.all') },
        { value: ConsolidationTaskStatus.CREATED, label: t('wms.baggingTask.status.created') },
        { value: ConsolidationTaskStatus.DISSOLVED, label: t('wms.baggingTask.status.discarded') },
        { value: ConsolidationTaskStatus.COMPLETED, label: t('wms.baggingTask.status.completed') },
    ];

    // Other Carrier页面的状态选项，包含 Handover
    const statusOptionsWithHandover = [
        { value: 'all', label: t('components.input.all') },
        { value: ConsolidationTaskStatus.CREATED, label: t('wms.baggingTask.status.created') },
        { value: ConsolidationTaskStatus.DISSOLVED, label: t('wms.baggingTask.status.discarded') },
        { value: ConsolidationTaskStatus.COMPLETED, label: t('wms.baggingTask.status.completed') },
        { value: ConsolidationTaskStatus.HANDOVER, label: t('wms.baggingTask.status.handover') },
    ];

    // 拉取全量 HUB/WHS 选项（与新建组包 Drawer 一致）
    const { data: hubWhsData, loading: optionsLoading } =
      useQuery<GetHubAndWhsOptionsResponse>(GET_HUB_AND_WHS_OPTIONS);

    // 获取Carrier选项（仅用于other-carrier页面）
    const { data: carrierData, loading: carrierLoading } = useQuery<GetCarrierOptionsResponse>(
        GET_CARRIER_OPTIONS,
        {
            skip: pageType !== 'other-carrier', // 只在other-carrier页面时查询
            variables: {
                includeSwiftX: false // other-carrier页面不需要包含SwiftX
            }
        }
    );

    // 从返回数据中拿到 hubs/whs 原始列表
    const hubs = hubWhsData?.getHubAndWhsOptions.hubOptions || [];
    const whsAll = hubWhsData?.getHubAndWhsOptions.whsOptions || [];

    // Carrier选项处理
    const carrierOptions = carrierData?.getCarrierOptions.map(carrier => ({
        value: carrier.value,
        label: carrier.name
    })) || [];

    // 以 “名称” 作为筛选值，与页面原有字段 hubName/whsName 对齐
    const hubOptions = [
      { value: '', label: t('components.input.all') },
      ...hubs.map(h => ({ value: h.hubName, label: h.hubName }))
    ];

    // 计算当前选中 HUB 的 hubId（用于过滤 WHS）
    const selectedHub = hubs.find(h => h.hubName === filters.hubName);
    const selectedHubId = selectedHub?.hubId;

    // WHS：未选 HUB → 全部；已选 HUB → 仅 parentHubId 匹配的
    const whOptionsSource = selectedHubId
      ? whsAll.filter(w => w.parentHubId === selectedHubId)
      : whsAll;

    const whOptions = [
      { value: '', label: t('components.input.all') },
      ...whOptionsSource.map(w => ({ value: w.whsName, label: w.whsName }))
    ];

    const handleFilterChange = (key: keyof ConsolidationFilterOptions, value: any) => {
        const newFilterOptions: ConsolidationFilterOptions = {
            ...filters,
            [key]: value
        };
        setFilters(newFilterOptions);
    };

    return (
        <div className="mb-2 pl-0 py-2">
            {/* 使用 Grid 布局确保列对齐 */}
            <div className="grid grid-cols-3 gap-x-10 gap-y-4 items-start">
                <div className="min-w-0">
                    <InputFilterItem
                        label={labelTaskNumber}
                        value={filters.taskNameSearch}
                        onChange={(e) => handleFilterChange('taskNameSearch', e.target.value)}
                        spanClassName={getSmartLabelClassName(col1Labels)}
                    />
                </div>
                <div className="min-w-0">
                    <ParcelFilterStyle
                        label={labelTaskStatus}
                        options={pageType === 'other-carrier' ? statusOptionsWithHandover : statusOptions}
                        value={filters.status}
                        onChange={(value: string) => handleFilterChange('status', (value || 'all') as ConsolidationTaskStatus | 'all')}
                        className="w-full"
                        spanClassName={getSmartLabelClassName(col2Labels)}
                    />
                </div>
                <div className="min-w-0">
                    <ParcelFilterStyle
                        label={labelDestinationHub}
                        options={hubOptions}
                        value={filters.hubName || ''}
                        onChange={(value: string) => {
                            // 切换 HUB 时重置 WHS
                            setFilters({ ...filters, hubName: value || '', whsName: '' });
                        }}
                        className="w-full"
                        spanClassName={getSmartLabelClassName(col3Labels)}
                    />
                </div>
                {/* 第二行 */}
                {pageType === 'swiftx' && (
                    <div className="min-w-0">
                        <ParcelFilterStyle
                            label={labelDestinationWhs}
                            options={whOptions}
                            value={filters.whsName || ''}
                            onChange={(value: string) => setFilters({ ...filters, whsName: value || '' })}
                            className="w-full"
                            spanClassName={getSmartLabelClassName(col2Labels)}
                        />
                    </div>
                )}
                {pageType === 'other-carrier' && (
                    <div className="min-w-0">
                        <ParcelFilterStyle
                            label={labelCarrier}
                            options={carrierOptions}
                            value={filters.carrierCode !== undefined ? String(filters.carrierCode) : undefined}
                            onChange={(value: string) => setFilters({ ...filters, carrierCode: value ? Number(value) : undefined })}
                            className="w-full"
                            spanClassName={getSmartLabelClassName(col3Labels)}
                            placeholder={t('wms.baggingTask.placeholders.carrier') || undefined}
                        />
                    </div>
                )}
                <div className="min-w-0">
                    <DateRangeFilterItem
                        label={labelCreationTime}
                        value={filters.createdAtRange ? [
                            filters.createdAtRange[0]?.format('YYYY/MM/DD') || null,
                            filters.createdAtRange[1]?.format('YYYY/MM/DD') || null
                        ] : null}
                        onChange={(dates) => {
                            const dayjsDates = dates ? [
                                dates[0] ? dayjs(dates[0]) : null,
                                dates[1] ? dayjs(dates[1]) : null
                            ] as [dayjs.Dayjs | null, dayjs.Dayjs | null] : [null, null];
                            handleFilterChange('createdAtRange', dayjsDates);
                        }}
                        className="w-full"
                        spanClassName={getSmartLabelClassName(col1Labels)}
                    />
                </div>
                <div className="min-w-0"></div>
            </div>
        </div>
    );
};

export default ConsolidationFilters; 